/**
 * BasePanel — abstract base for every management panel.
 *
 * Provides:
 *  - DOM creation (ornate frame with header + tabs + body + footer)
 *  - open/close lifecycle with CSS transitions
 *  - tab switching
 *  - automatic tick subscription while open
 *  - clean teardown
 *
 * Usage:
 *   class MyPanel extends BasePanel {
 *     _render() { this.body.innerHTML = '...'; }
 *   }
 *
 * @module ui/panels/BasePanel
 */

import { bus } from '../../core/events.js';

export class BasePanel {
  /**
   * @param {object} opts
   * @param {string} opts.name          — unique panel id (e.g. 'hero')
   * @param {string} opts.title         — header title text
   * @param {Array<{id:string, label:string}>} [opts.tabs] — tab definitions
   * @param {object} opts.state         — game state reference
   * @param {object} opts.data          — data tables reference
   * @param {object} [opts.hooks]       — { onChange?, onChooseClass?, ... }
   */
  constructor(opts) {
    this.name = opts.name;
    this._title = opts.title;
    this._tabs = opts.tabs ?? [];
    this.state = opts.state;
    this.data = opts.data;
    this.hooks = opts.hooks ?? {};

    this._activeTab = this._tabs[0]?.id ?? null;
    this._forcedOpen = false;
    this._open = false;
    this._subs = [];

    this._buildDOM();
  }

  // ---- Subclass overrides ----

  /** Override: rebuild the panel body content. Called on open + on every tick. */
  render() { /* no-op */ }

  /** Override: called when the panel is about to open. */
  onOpen() { /* no-op */ }

  /** Override: called when the panel is about to close. */
  onClose() { /* no-op */ }

  /** Override: called after the panel is destroyed (removed from DOM). */
  onDestroy() { /* no-op */ }

  // ---- DOM construction ----

  _buildDOM() {
    this.root = document.createElement('div');
    this.root.className = 'pd-panel';
    this.root.setAttribute('data-panel', this.name);
    this.root.hidden = true;

    // Header
    const header = document.createElement('div');
    header.className = 'pd-panel-header';

    if (this._tabs.length) {
      const tabsEl = document.createElement('div');
      tabsEl.className = 'pd-panel-tabs';
      for (const tab of this._tabs) {
        const btn = document.createElement('button');
        btn.className = 'pd-panel-tab';
        btn.textContent = tab.label;
        btn.dataset.tab = tab.id;
        btn.addEventListener('click', () => this.switchTab(tab.id));
        tabsEl.appendChild(btn);
      }
      header.appendChild(tabsEl);
    }

    const titleEl = document.createElement('span');
    titleEl.className = 'pd-panel-title';
    titleEl.textContent = this._title;
    header.appendChild(titleEl);

    const closeBtn = document.createElement('button');
    closeBtn.className = 'pd-panel-close';
    closeBtn.textContent = '✕';
    closeBtn.title = 'Fechar';
    closeBtn.addEventListener('click', () => this._requestClose());
    header.appendChild(closeBtn);
    this._closeBtn = closeBtn;

    this.root.appendChild(header);

    // Body
    this.body = document.createElement('div');
    this.body.className = 'pd-panel-body';
    this.root.appendChild(this.body);

    // Footer (optional, hidden by default; subclasses can show it)
    this.footer = document.createElement('div');
    this.footer.className = 'pd-panel-footer';
    this.footer.hidden = true;
    this.root.appendChild(this.footer);

    document.getElementById('pd-panels').appendChild(this.root);
  }

  // ---- Lifecycle ----

  /** @internal Called by PanelManager */
  _openInternal(opts = {}) {
    this._forcedOpen = !!opts.forced;
    this._open = true;

    if (this._forcedOpen) {
      this._closeBtn.style.visibility = 'hidden';
    } else {
      this._closeBtn.style.visibility = 'visible';
    }

    this.onOpen();
    this.render();
    this._refreshTabs();

    this.root.hidden = false;
    // Force layout then animate
    this.root.offsetHeight;
    this.root.classList.add('open');

    // Subscribe to tick for auto-refresh
    this._subs.push(bus.on('tick', () => {
      if (this._open) this.render();
    }));

    // Focus for keyboard
    this.root.focus();
  }

  /** @internal Called by PanelManager or internally */
  _closeInternal() {
    if (!this._open) return;

    this._open = false;
    this._forcedOpen = false;

    this.root.classList.remove('open');

    // Wait for transition then hide
    const onTransitionEnd = () => {
      this.root.removeEventListener('transitionend', onTransitionEnd);
      if (!this._open) {
        this.root.hidden = true;
        this._closeBtn.style.visibility = 'visible';
      }
    };
    this.root.addEventListener('transitionend', onTransitionEnd);
    // Fallback in case transition doesn't fire
    setTimeout(() => {
      if (!this._open) {
        this.root.hidden = true;
        this._closeBtn.style.visibility = 'visible';
      }
    }, 400);

    this._subs.forEach((off) => off());
    this._subs = [];

    this.onClose();
  }

  _requestClose() {
    if (this._forcedOpen) return;
    // Emit event — PanelManager listens and calls close()
    bus.emit('panel:requestClose', { name: this.name });
  }

  /** Bring this panel to the top z-index. */
  focus() {
    // Query within whichever document the panel currently lives in (the main
    // page, or the native PiP window when relocated there).
    const doc = this.root.ownerDocument;
    let z = parseInt(getComputedStyle(doc.documentElement).getPropertyValue('--z-panel')) || 110;
    // Increment from all other panels
    const panels = doc.querySelectorAll('.pd-panel.open');
    panels.forEach((p, i) => { p.style.zIndex = z + i; });
    this.root.style.zIndex = z + panels.length + 1;
  }

  // ---- Tab switching ----

  switchTab(tabId) {
    this._activeTab = tabId;
    this._refreshTabs();
    this.render();
  }

  _refreshTabs() {
    const tabs = this.root.querySelectorAll('.pd-panel-tab');
    tabs.forEach((btn) => {
      btn.classList.toggle('active', btn.dataset.tab === this._activeTab);
    });
  }

  get activeTab() { return this._activeTab; }

  // ---- Helpers for subclasses ----

  /** Emit a change event so autosave triggers, then re-render. */
  commit() {
    this.hooks.onChange?.();
    this.render();
  }

  /** Resolve rarity colour from data tables. */
  rarityColor(rarity) {
    return this.data.rarity?.[rarity]?.color ?? '#9d9d9d';
  }

  /** Format an item tooltip string. */
  itemTooltip(item) {
    return `${item.label} (Nv.${item.itemLevel})\n` +
      item.affixes.map((a) => a.label).join('\n');
  }

  // ---- Cleanup ----

  destroy() {
    this._closeInternal();
    this._subs.forEach((off) => off());
    this._subs = [];
    this.onDestroy();
    if (this.root && this.root.parentNode) {
      this.root.parentNode.removeChild(this.root);
    }
  }
}
